import React, { useState, useEffect } from "react";
import { FiX, FiCheck } from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";

/**
 * SettleVendorOpeningBalanceModal — record a payment against a vendor's
 * opening balance (the amount we owed them before the system started).
 *
 * Creates a vendor payment via POST /api/vendors/payments/create with
 * ob_allocated_amount. It is a stand-alone payment — never linked to
 * purchases. To pay purchases, record a separate payment.
 *
 * Props
 *   vendor   – { id, company_name }
 *   summary  – { amount, settled, remaining, date }  (GET /vendors/:id/opening-balance)
 *   onClose  – close the popup
 *   onSaved  – called with the fresh summary after saving
 */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 };
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px",
  border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none",
  fontFamily: "inherit",
};

const SettleVendorOpeningBalanceModal = ({ vendor, summary, onClose, onSaved }) => {
  const remaining = Number(summary?.remaining || 0);
  const minDate = summary?.date ? String(summary.date).slice(0, 10) : "";

  const [form, setForm] = useState({
    amount: remaining > 0 ? String(remaining) : "",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "cash",
    bank_account_id: "",
    reference_number: "",
    notes: "",
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    API.get("/company-bank-accounts", { params: { active: "true" } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  const amount = Number(form.amount);
  const afterBalance = Math.max(0, remaining - (Number.isFinite(amount) ? amount : 0));

  const handleSave = async () => {
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Enter the amount paid.");
    if (amount > remaining + 0.009) return toast.error(`Amount cannot exceed the pending ${money(remaining)}.`);
    if (!form.payment_date) return toast.error("Select the payment date.");
    if (minDate && form.payment_date < minDate) return toast.error(`Date cannot be before the opening balance date (${minDate}).`);

    try {
      setSaving(true);
      const { data } = await API.post("/vendors/payments/create", {
        vendor_id:           vendor.id,
        ob_allocated_amount: amount,
        payment_date:        form.payment_date,
        payment_method:      form.payment_method,
        bank_account_id:     form.bank_account_id || null,
        reference_number:    form.reference_number,
        notes:               form.notes,
      });
      toast.success(`Payment ${data.payment?.payment_number || ""} saved — opening balance settled`);
      onSaved && onSaved(data.openingBalance);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 26, width: 500, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Settle Opening Balance</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{vendor.company_name}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}><FiX size={18} /></button>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 13 }}>
          <span>Opening: <strong>{money(summary?.amount)}</strong></span>
          <span style={{ color: "#16a34a" }}>Paid: <strong>{money(summary?.settled)}</strong></span>
          <span style={{ color: "#dc2626" }}>Pending: <strong>{money(remaining)}</strong></span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Amount Paid (₹) *</label>
            <input type="number" min="0" max={remaining} step="0.01" value={form.amount}
              onChange={(e) => set("amount", e.target.value)} placeholder={`Max ${money(remaining)}`} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date *</label>
            <input type="date" value={form.payment_date} min={minDate || undefined}
              onChange={(e) => set("payment_date", e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Payment Method</label>
            <select value={form.payment_method} onChange={(e) => { set("payment_method", e.target.value); set("bank_account_id", ""); }} style={{ ...inputStyle, background: "#fff" }}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Reference / UTR No.</label>
            <input type="text" value={form.reference_number} onChange={(e) => set("reference_number", e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Paid From Account</label>
            <select value={form.bank_account_id} onChange={(e) => set("bank_account_id", e.target.value)} style={{ ...inputStyle, background: "#fff" }}>
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter((a) => {
                  if (form.payment_method === "cash") return a.account_type === "cash";
                  if (form.payment_method === "upi") return a.account_type === "upi";
                  if (form.payment_method === "bank_transfer" || form.payment_method === "cheque") return a.account_type === "bank";
                  return true;
                })
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ""}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Notes</label>
            <input type="text" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>
        </div>

        {amount > 0 && (
          <div style={{ fontSize: 12.5, color: amount > remaining + 0.009 ? "#dc2626" : "#374151", marginBottom: 12 }}>
            Settles <strong>{money(amount)}</strong> of the opening balance · pending after: <strong>{money(afterBalance)}</strong>
          </div>
        )}

        <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 14 }}>
          This payment only settles the opening balance. To pay purchases, record a separate payment.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "9px 18px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 14, fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#16a34a", color: "#fff", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontSize: 14, display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
            <FiCheck size={14} /> {saving ? "Saving…" : "Save Payment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettleVendorOpeningBalanceModal;
